import type { Bilingual } from '@matix/types';
import type { Lang } from '../../lib/language';

/**
 * Matching a geocoder's place name against the tenant's own lookup rows.
 *
 * Kept apart from AddressLookup.tsx (which pulls in React and the API client)
 * because this is the part with real edge cases and it deserves plain unit
 * tests — see __tests__/addressMatch.test.ts.
 *
 * The bar is deliberately high: an unmatched name shows an explicit "add it"
 * button, which costs the cashier one tap. A *wrong* match silently files the
 * customer under the wrong district, and nobody notices until a driver is lost.
 */

/**
 * Prefer the reader's script, fall back to the other one. A whitespace-only
 * value counts as absent — Google hands back the odd empty component, and a
 * blank string would otherwise beat a perfectly good name in the other script.
 */
export function pickName(b: Bilingual | null | undefined, lang: Lang): string | null {
  if (!b) return null;
  const en = b.en?.trim() || null;
  const ar = b.ar?.trim() || null;
  return (lang === 'ar' ? ar ?? en : en ?? ar) ?? null;
}

/**
 * Fold a place name to something comparable across sources.
 *
 * Google writes "Al Malqa" / "الملقا"; a tenant row might be "الملقاء" or
 * "حي الملقا". Normalising alef/ya/ta-marbuta and dropping the article plus the
 * classifier word ("district", "حي") gets those to meet.
 */
export function normalizePlaceName(s: string | null | undefined): string {
  const folded = (s ?? '')
    .toLowerCase()
    .replace(/[ً-ْـ]/g, '') // harakat + tatweel
    .replace(/[أإآٱ]/g, 'ا')
    .replace(/ى/g, 'ي')
    .replace(/ؤ/g, 'و')
    .replace(/ئ/g, 'ي')
    .replace(/ة/g, 'ه')
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  // Classifier words carry no identity ("Al Malqa" vs "Al Malqa District" are
  // the same place). Dropped after folding so ة→ه has already happened.
  const stripped = folded
    .replace(/\b(district|neighborhood|neighbourhood|governorate|province|region|city)\b/g, ' ')
    .replace(/(^|\s)(حي|منطقه|محافظه|مدينه)(\s|$)/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  // The definite article, last — it can only be recognised once the classifier
  // in front of it ("حي الملقا") is gone.
  return stripped
    .replace(/^(al|el)[\s-]+/, '')
    .replace(/^ال/, '')
    .trim();
}

export interface NamedOption {
  id: number;
  name: string | null;
  name_ar: string | null;
}

/**
 * Find the lookup row a geocoded name refers to. Exact (normalised) match
 * first; then containment, but only when exactly one row contains it —
 * "الرياض" legitimately sits inside "شمال الرياض", while a loose match against
 * several rows is a coin flip we shouldn't take on the cashier's behalf.
 *
 * Returns null when nothing is confidently the same place.
 */
export function matchLookupOption<T extends NamedOption>(
  options: T[],
  wanted: Bilingual | null | undefined,
): T | null {
  if (!wanted) return null;
  const needles = [normalizePlaceName(wanted.ar), normalizePlaceName(wanted.en)].filter(
    (n) => n.length >= 2,
  );
  if (needles.length === 0) return null;

  for (const needle of needles) {
    const exact = options.find(
      (o) =>
        normalizePlaceName(o.name_ar) === needle || normalizePlaceName(o.name) === needle,
    );
    if (exact) return exact;
  }
  for (const needle of needles) {
    if (needle.length < 3) continue;
    const partial = options.filter((o) => {
      const ar = normalizePlaceName(o.name_ar);
      const en = normalizePlaceName(o.name);
      return (
        (ar.length >= 3 && (ar.includes(needle) || needle.includes(ar))) ||
        (en.length >= 3 && (en.includes(needle) || needle.includes(en)))
      );
    });
    if (partial.length === 1) return partial[0] ?? null;
  }
  return null;
}
